import React, { useState } from 'react';
import { Search, BookOpen, Video, MessageCircle, HelpCircle, ChevronRight, ExternalLink, Shield, Compass, Monitor } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useOnboarding } from '../contexts/OnboardingContext';

interface HelpArticle {
  id: string;
  title: string;
  category: string;
  description: string;
  content: string;
  tags: string[];
  lastUpdated: string;
  type: 'article' | 'video' | 'tutorial';
}

const HelpPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedArticle, setSelectedArticle] = useState<HelpArticle | null>(null);
  const { startTour } = useOnboarding();

  const formatHelpContent = (content: string): string => {
    return content
      // Images: ![alt](src) — rendered as a captioned, bordered screenshot.
      // Uses span (inline) elements so the markup stays valid inside auto-generated <p> tags.
      .replace(
        /!\[([^\]]*)\]\(([^)]+)\)/g,
        '<span class="block my-6"><img src="$2" alt="$1" loading="lazy" class="rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm w-full" /><span class="block text-center text-sm text-gray-500 dark:text-gray-400 mt-2 italic">$1</span></span>'
      )

      // Blockquote callouts: lines starting with "> "
      .replace(/^> (.*$)/gm, '<blockquote class="border-l-4 border-accent-color-400 bg-accent-color-50 dark:bg-accent-color-900/20 pl-4 pr-3 py-3 my-4 rounded-r text-gray-700 dark:text-gray-300">$1</blockquote>')

      // Headers
      .replace(/^# (.*$)/gm, '<h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-6 mt-8 first:mt-0">$1</h1>')
      .replace(/^## (.*$)/gm, '<h2 class="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4 mt-6">$1</h2>')
      .replace(/^### (.*$)/gm, '<h3 class="text-xl font-medium text-gray-900 dark:text-gray-100 mb-3 mt-5">$1</h3>')
      
      // Bold text
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-gray-900 dark:text-gray-100">$1</strong>')
      
      // Lists
      .replace(/^\- (.*$)/gm, '<li class="ml-4 mb-2 text-gray-700 dark:text-gray-300">• $1</li>')
      .replace(/^(\d+)\. (.*$)/gm, '<li class="ml-4 mb-2 text-gray-700 dark:text-gray-300">$1. $2</li>')
      
      // Code blocks
      .replace(/`(.*?)`/g, '<code class="bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 px-2 py-1 rounded text-sm font-mono">$1</code>')
      
      // Line breaks and paragraphs
      .replace(/\n\n/g, '</p><p class="mb-4 text-gray-700 dark:text-gray-300 leading-relaxed">')
      .replace(/\n/g, '<br>')
      
      // Wrap in paragraph tags
      .replace(/^(?!<[h|l])/gm, '<p class="mb-4 text-gray-700 dark:text-gray-300 leading-relaxed">')
      .replace(/(?<!>)$/gm, '</p>')
      
      // Clean up empty paragraphs
      .replace(/<p class="mb-4 text-gray-700 dark:text-gray-300 leading-relaxed"><\/p>/g, '')
      .replace(/<p class="mb-4 text-gray-700 dark:text-gray-300 leading-relaxed"><br><\/p>/g, '')
      
      // Wrap lists properly
      .replace(/(<li class="ml-4 mb-2 text-gray-700 dark:text-gray-300">.*<\/li>)/gs, '<ul class="mb-4 space-y-1">$1</ul>')
      .replace(/<\/ul><ul class="mb-4 space-y-1">/g, '');
  };

  // Sample help articles - in a real app, these would come from a CMS or API
  const helpArticles: HelpArticle[] = [
    {
      id: 'new-hire-guide',
      title: 'New Hire Guide: Your First Work Order',
      category: 'Getting Started',
      description: 'A visual, step-by-step walkthrough for brand-new users — from adding a client to creating and invoicing a work order.',
      content: `
Welcome to WorkOrder HQ! This guide walks you through the app from scratch and ends with you creating your very first work order. No prior experience needed — just follow along.

## Core concepts (read this first)

Four words make everything else click:

- **Client** — the company/customer you do work for. Every work order belongs to a client.
- **Location** — a specific address belonging to a client (e.g., "Main Store", "Warehouse"). Work happens at a location, and **every client must have at least one**.
- **Vendor** — a contractor who performs the work (HVAC tech, plumber, etc.). Vendors are **optional** on a work order.
- **Work Order** — a single job. This is the heart of the app; everything connects to it.

> **The golden rule:** Before you can create a work order, the client and at least one of its locations must already exist. That's why this guide adds a client (and vendors) **first**.

## Getting around

After you log in, you land on the **Dashboard** — your home base. The left sidebar is your main navigation, grouped into sections like **Primary Operations**, **Business Management**, and **Insights & Support**.

![The Dashboard is your home base — stat cards up top, your work orders below.](/help-screenshots/dashboard.png)

The dashboard stat cards (My Work Orders, New Work, Unassigned, etc.) are clickable filters — click one to filter the list of work orders below it.

## Step 1: Add a client (and its first location)

A client and its first location are created together in one short wizard.

1. In the sidebar, click **Clients**, then click **Add Client** (top right).
2. **Step 1 — Client Information:** enter the **Name** (required). Phone, Email, and Rank are optional.
3. Click **Next**.
4. **Step 2 — Primary Location:** enter a **Location Name** and **Type** (required), plus the address and contact details.
5. Click **Create Client**.

![The Add Client wizard — Step 1 collects basic client details, Step 2 collects the first location.](/help-screenshots/add-client-wizard.png)

Your clients all live on the Clients page, where you can search and see each client's primary location, rank, and status.

![The Clients list — search, primary location, rank, and status at a glance.](/help-screenshots/clients-list.png)

> **Tip:** To add more locations later, open the client and click **Edit Client** — locations are managed there, not on the read-only detail page. Address fields are required when adding locations this way.

## Step 2: Add vendors

Vendors are the contractors who'll do the work. You can skip this and add them later, but let's add one now.

1. In the sidebar, click **Vendors**, then **Add Vendor**.
2. Fill in the required fields: **Name**, **Phone**, **Address**, **City**, **State**, **Zip Code**.
3. Check at least one **Category** (e.g., HVAC, Plumbing) — **at least one is required**.
4. Optionally add Email, Website, Rank, **Rating** (0–5 stars), and internal Notes.
5. Click **Create Vendor**.

![The Vendors list — categories, ratings, and compliance status.](/help-screenshots/vendors-list.png)

> **Why categories and zip matter:** When you build a work order, the app finds vendors by **zip code + search radius + category**. A vendor with no category, or one far outside the radius, won't appear in the search. Set these correctly so vendors are findable.

## Step 3: Create the work order

This is the main event. Creating a work order is a **4-step wizard**. Click **Work Orders** in the sidebar, then **Create Work Order**.

![Creating a work order is a guided 4-step wizard: Client → Vendor → Details → Complete.](/help-screenshots/create-wo-step1.png)

### Step 1 of 4 — Client Selection
- **Select Client**, then **Select Location** (appears after you pick a client).
- Set the **Zipcode (Vendor Search Area)** and **Search Radius** (both required).
- Check at least one **Service Category**.
- Click **Next Step** (the button stays disabled until client, location, radius, and a category are set).

### Step 2 of 4 — Vendor Selection (optional)
- The app lists vendors matching your zip, radius, and categories.
- Check one or more to assign them. The first one becomes the **Primary** vendor.
- You can continue with **zero vendors** and assign them later.

### Step 3 of 4 — Work Details
- Set **Priority**, **Status**, and **Due Date** (required).
- Optional: Assigned User, Category, Equipment, External WO Number, Template, **NTE** (spending cap), and Description.

### Step 4 of 4 — Complete
- Review the preview and click **Save & Create Work Order**. Done!

> **Create vs. Edit heads-up:** On the create wizard, Assigned User and Description are optional. When you later **Edit** the work order, both become **required** (Description needs 10+ characters). It's good practice to fill them in up front.

All your jobs live on the Work Orders list, with search, status tabs, sortable columns, and a Grid/Calendar toggle.

![The Work Orders list — status tabs, priority, due dates, and days remaining.](/help-screenshots/workorders-list.png)

## Step 4: Work the job on the Job Contact Sheet

Click any work order row to open its **Job Contact Sheet** — the day-to-day workspace for that job.

![The Job Contact Sheet — timeline, files, financials, and quick contact info.](/help-screenshots/job-contact-sheet.png)

It has three tabs plus a sidebar:

- **Timeline** — the running history of the job. Click **Add Timeline Entry** to log a Note, Call, File Upload, a billable **Include on Invoice** item, a Status Update, and more. The timeline auto-refreshes every 30 seconds.
- **Files** — all attachments for the job.
- **Financial** — invoice summary and the **Create Invoice** options once the job is ready.

The right **sidebar** has Quick Contact info and Quick Info checkboxes (Signed Off, Quote Requested, etc.) that **save the moment you click them** — there's no separate Save button.

> **Tip:** Log **Include on Invoice** timeline entries as you go. When it's time to bill, those items are ready to drop onto the invoice instead of retyping everything.

## Step 5: Invoice and record payment

When the job is ready, open the **Financial** tab and click **Create Invoice** (Deposit, Progress, Final, or Change Order). Review the line items, tax, and discount, then **Save** and **Mark as Sent**. To log a payment, use **Manage Payments** → **Record Payment**.

![The Invoices page — status filters, date ranges, and at-a-glance totals.](/help-screenshots/invoices-list.png)

> **Note:** Marking an invoice "Sent" updates its status; it does not auto-email the client today. Download the PDF and send it through your normal channel.

## Quick tips & gotchas

- **Setup order matters:** always go Client → Location → (Vendor) → Work Order.
- **Clicking a work order opens the Job Contact Sheet**, not the edit form. Use the **Edit** button there to change core fields.
- **Due dates** can't be in the past; new work orders default to 7 days out.
- **Can't see a menu item?** Some areas are gated by your **role** (Admin-only), your **plan** (Invoices/Reports/Compliance), or **company type** (Units for property management). Ask your company Admin if you think you should have access.

That's the whole loop! The best way to learn is to run through these five steps once end-to-end with a test client. Welcome aboard.
      `,
      tags: ['getting started', 'new hire', 'work order', 'walkthrough', 'onboarding'],
      lastUpdated: '2026-06-24',
      type: 'tutorial'
    },
    {
      id: 'export-your-data',
      title: 'How to Export Your Data',
      category: 'Data & Security',
      description: 'Learn how to download a complete backup of all your company data',
      content: `
# How to Export Your Data

Export your company data at any time to create backups or prepare for migration.

## Why Export Your Data?

- **Peace of Mind**: Keep your own backup of all company information
- **Compliance**: Maintain records for accounting, legal, or regulatory requirements
- **Portability**: Migrate your data to another system if needed
- **Business Continuity**: Protect against accidental data loss

## Step-by-Step Guide

### Step 1: Access Your Profile
1. Log in to WorkOrder HQ
2. Click on your profile in the top right corner
3. Select **"User Profile"** from the dropdown menu

### Step 2: Initiate the Export
1. Scroll down to the **"Export Your Data"** section
2. Click the **"Export All Data"** button
3. Wait for the export to complete (typically 1-2 minutes)

### Step 3: Download Your Data
1. Once complete, you'll see a progress modal with a download button
2. Click **"Download Export File"** to save the ZIP file to your computer
3. Save the file in a safe location

## Understanding Your Export File

Your export file is a ZIP archive containing:

- **export.json**: All your structured data in JSON format
- **export-metadata.json**: Summary statistics and export information
- **README.txt**: Instructions for opening and using your data
- **workorders/**: All work order attachments
- **uploads/**: Timeline attachments and photos

## Previous Exports

Your profile page maintains a list of all previous exports:
- View when each export was created
- See file size and expiration date
- Download any previous export (if not expired)
- Delete old exports you no longer need

**Note**: Export links expire after 7 days for security reasons.

## Best Practices

- **Export regularly**: Monthly or quarterly for compliance records
- **Before major changes**: Export before deleting old data or making bulk changes
- **End of projects**: Export at the end of projects for archiving
- **Before cancellation**: Export before cancelling your subscription

## Storage Recommendations

- **Local Computer**: Keep on your main computer or server
- **Cloud Storage**: Upload to Google Drive, Dropbox, or OneDrive
- **External Drive**: Copy to external hard drives for offline backup
- **Network Storage**: Store on your company's network server

## Security Notes

- Export files contain sensitive business information
- Store them securely and limit access
- Consider encrypting the ZIP file for added security
- Delete old export files when no longer needed
      `,
      tags: ['export', 'backup', 'data', 'security'],
      lastUpdated: '2024-01-17',
      type: 'article'
    },
    {
      id: 'whats-included-export',
      title: "What's Included in Your Export?",
      category: 'Data & Security',
      description: 'Complete breakdown of what data is included in your export',
      content: `
# What's Included in Your Export?

Your complete data export includes everything you need for a comprehensive backup.

## Core Business Data

### Work Orders
- All work orders with full history
- Descriptions and metadata
- Priority, status, and category information
- Due dates and assignment details
- All work order attachments

### Clients & Locations
- Complete client information
- Contact details (email, phone, address)
- All client locations
- Primary location flags
- Tax rates and billing information

### Vendors
- All vendor records
- Contact information
- Categories and specializations
- Address and location data
- Vendor rankings

### Invoices
- Invoice records and line items
- Payment history
- Billing dates and terms
- Tax calculations
- Client billing information

### Financial Records
- **Credit Memos**: Credit memo records and applications
- **Payments**: Payment transaction history
- Payment methods and reference numbers

## User & Settings Data

### User Accounts
- User profiles and contact information
- Role assignments
- Account status
- Profile photos
- Account creation dates

**Note**: Passwords and security tokens are never included in exports for security reasons.

### Company Settings
- Company profile and configuration
- Logo and branding
- System preferences

## Timeline & Communication

### Job Contact Entries
- All timeline entries (job contact sheets)
- User notes and messages
- Entry types and timestamps
- Internal vs. external entries
- Vendor communications

### Attachments
- **Work Order Attachments**: All files uploaded to work orders
- **Timeline Attachments**: Photos, documents, and files from job contact entries
- **Profile Photos**: User and company profile images

## File Structure

Your export ZIP file is organized like this:

**WorkOrderExport_YourCompany_20240115.zip**
- \`README.txt\` - Instructions and overview
- \`export.json\` - All structured data
- \`export-metadata.json\` - Summary statistics
- \`workorders/\` - Work order attachments
  - \`[work_order_id]/attachments/\` - Files for each work order
- \`uploads/\` - Timeline attachments
  - Various files from job contact entries

## Data Format

- **JSON Format**: All data is in standard JSON format for easy processing
- **Readable**: You can open and read the JSON files directly
- **Importable**: Data structure is documented for potential import into other systems

## What's NOT Included

For security and privacy reasons:
- **Password Hashes**: Never included
- **Security Tokens**: Authentication tokens are excluded
- **Payment Card Data**: Credit card information is never stored or exported
- **Third-Party Data**: Data from external services is not included
      `,
      tags: ['export', 'data', 'structure', 'included'],
      lastUpdated: '2024-01-17',
      type: 'article'
    },
    {
      id: 'data-retention-cancellation',
      title: 'Data Retention After Cancellation',
      category: 'Data & Security',
      description: 'Understand what happens to your data when you cancel your subscription',
      content: `
# Data Retention After Cancellation

Understand your data retention rights and timeline when cancelling your subscription.

## Grace Period

### 60-Day Retention
- Your data is retained for **60 days** after cancellation
- Access is limited: You can export data but cannot make changes
- All features remain available for data export purposes

### What Happens During This Time?

- **Export Access**: You can export your data at any time
- **No Modifications**: You cannot create or edit work orders, invoices, etc.
- **Read-Only Mode**: Your account is in read-only mode
- **No New Users**: Cannot add new users or make account changes

## Warning Timeline

### First Warning (Day 53)
- You'll receive an email reminder **7 days before deletion**
- Clear instructions on how to export your data
- Direct link to the export feature

### Final Warning
- Urgent reminder about impending deletion
- Emphasis on the irreversibility of data deletion
- Step-by-step export instructions

## Email Notifications

You'll receive several emails during the grace period:

1. **Cancellation Confirmation**: Immediately after cancellation with retention policy details
2. **7-Day Warning**: Final reminder before permanent deletion
3. **Deletion Confirmation**: Confirmation after data is permanently deleted

All emails include:
- Timeline information
- Export instructions
- Support contact information

## Permanent Deletion

### What Happens
- After 60 days, all data is permanently deleted
- **Cannot be recovered** once deleted
- Account access is completely removed
- All files and attachments are deleted

### What Gets Deleted
- All work orders and history
- Client and vendor information
- Invoices and payment records
- All uploaded files and photos
- User accounts and settings
- Company profile and configuration

## Preventing Deletion

### Reactivate Your Subscription
If you change your mind:
- Contact support to reactivate your subscription
- No data loss if done before day 60
- All your data will be preserved
- Normal access restored immediately

### Extend Grace Period
- Support may extend the grace period in special circumstances
- Contact support with your request
- Typically granted for valid business reasons
- Case-by-case evaluation

## Export Before Cancellation

### Best Practice
**Always export your data before cancelling** to ensure you have a complete backup:

1. Go to **User Profile** → **Export Your Data**
2. Download the complete export file
3. Verify the file opens correctly
4. Store it securely
5. Then proceed with cancellation

### Why This Matters
- You'll have immediate access to your backup
- No waiting for the 60-day period
- Complete peace of mind
- Ready for any migration needs

## FAQ

### Q: Can I get my data back after deletion?
**A**: No. Once data is permanently deleted after 60 days, it cannot be recovered.

### Q: What if I need more than 60 days?
**A**: Contact support immediately. We may be able to extend the grace period on a case-by-case basis.

### Q: Will I be notified multiple times?
**A**: Yes. You'll receive cancellation confirmation, a 7-day warning, and deletion confirmation.

### Q: Can I cancel and reactivate multiple times?
**A**: Yes, but your data is only retained during the grace period of each cancellation.

### Q: What if I exported my data but lost the file?
**A**: You can create a new export during the grace period. Contact support if you need assistance.
      `,
      tags: ['cancellation', 'retention', 'data', 'export'],
      lastUpdated: '2024-01-17',
      type: 'article'
    },
    {
      id: 'invoice-overdue-workflow',
      title: 'Managing Overdue Invoices',
      category: 'Invoices',
      description: 'Learn how to handle overdue invoices and take appropriate actions',
      content: `
# Managing Overdue Invoices

When an invoice becomes overdue, you can now take several actions to resolve the outstanding balance. This new workflow allows you to actively manage collections and maintain better cash flow.

## What You Can Do

### 1. Edit the Invoice
- **Update payment terms** if the client requests an extension
- **Modify amounts** if there are disputes or adjustments needed  
- **Change due dates** to accommodate client requests
- **Add notes** about payment arrangements

### 2. Record Payments
- Click **"Record Payment"** to log any payments received
- Handle partial payments and update invoice status automatically
- Track payment history and receipts
- Generate payment confirmations

### 3. Create Credit Memos
- Use **"Create Credit Memo"** for disputes or adjustments
- Apply credits to reduce the outstanding balance
- Maintain proper accounting records
- Handle refunds and corrections

### 4. Send Reminders
- Use **"Send Reminder"** to follow up with clients
- Maintain professional communication
- Keep track of follow-up attempts
- Automate reminder sequences

## Best Practices

1. **Act quickly** when invoices become overdue
2. **Document all communications** with clients
3. **Be flexible** with payment terms when appropriate
4. **Maintain records** of all actions taken
5. **Follow up regularly** until resolved

## Status Changes

- **Overdue** → **Partial**: When partial payment is received
- **Overdue** → **Paid**: When full payment is received  
- **Overdue** → **Cancelled**: If invoice is written off

## Pro Tips

- Use the **notification system** to stay on top of overdue invoices
- Set up **payment reminders** to automate follow-ups
- Review **aging reports** regularly to identify problem clients
- Consider **payment plans** for large outstanding balances
      `,
      tags: ['invoice', 'overdue', 'payments', 'workflow'],
      lastUpdated: '2024-01-17',
      type: 'article'
    },
    {
      id: 'bulk-invoice-operations',
      title: 'Bulk Invoice Operations',
      category: 'Invoices',
      description: 'How to perform bulk actions on multiple invoices',
      content: `
# Bulk Invoice Operations

Save time by performing actions on multiple invoices at once. This powerful feature allows you to manage dozens of invoices efficiently.

## Available Bulk Actions

### Mark as Sent
- Select multiple **Draft** invoices
- Click **"Mark as Sent"** to update their status instantly
- Optionally send email notifications to clients
- Perfect for monthly invoice batches

### Archive Invoices
- Select multiple **Paid** invoices
- Click **"Archive Selected"** to move them to archived status
- Helps keep your active invoice list clean and organized
- Maintains historical records for reporting

### Export to CSV
- Select invoices you want to export
- Click **"Export to CSV"** to download comprehensive data
- Includes all invoice details, payment information, and client data
- Compatible with Excel and other spreadsheet applications

## How to Use

1. **Select invoices** using the checkboxes in the invoice grid
2. **Choose your action** from the bulk actions bar that appears
3. **Review the results** - success/failure counts are shown clearly
4. **Handle any errors** that may occur with specific invoices

## Pro Tips

- Use **"Select All"** to quickly select all visible invoices
- **Clear Selection** to start over if needed
- Check the **status requirements** for each bulk action
- **Filter first** to narrow down your selection before bulk actions
- **Export regularly** to maintain backup copies of your data
      `,
      tags: ['bulk', 'invoices', 'export', 'archive'],
      lastUpdated: '2024-01-17',
      type: 'article'
    },
    {
      id: 'invoice-health-check',
      title: 'Invoice Health Check System',
      category: 'Invoices',
      description: 'Understanding the automatic invoice monitoring system',
      content: `
# Invoice Health Check System

The system automatically monitors your invoices and alerts you to issues.

## What It Does

### Automatic Monitoring
- **Checks daily** for overdue invoices
- **Identifies** invoices due within 7 days
- **Updates statuses** from Sent/Partial to Overdue when past due
- **Creates notifications** for each issue found

### Notification Types
- **Overdue alerts**: "Invoice #INV-00036 is overdue by 4 days"
- **Due soon alerts**: "Invoice #INV-00037 is due in 3 days"
- **Severity levels**: Warning for overdue, Info for due soon

## How to Use Notifications

### Notification Bell
- **Red number** shows count of unread notifications
- **Click the bell** to see recent notifications
- **Click invoice numbers** to go directly to the invoice

### Dashboard Banner
- **Orange banner** appears when issues are found
- **Summary information** shows counts and amounts
- **Quick actions** to view invoices or take action

## Best Practices

1. **Check notifications daily** to stay on top of issues
2. **Act quickly** on overdue invoices
3. **Use the workflow banners** in invoice editor
4. **Keep client information updated** for accurate notifications
      `,
      tags: ['health-check', 'notifications', 'monitoring', 'alerts'],
      lastUpdated: '2024-01-17',
      type: 'article'
    },
    {
      id: 'aging-reports',
      title: 'Invoice Aging Reports',
      category: 'Reports',
      description: 'Generate and understand invoice aging reports',
      content: `
# Invoice Aging Reports

Track outstanding invoices by age to better manage your cash flow.

## What It Shows

### Age Buckets
- **Current**: Not yet due
- **1-30 Days**: Recently overdue
- **31-60 Days**: Moderately overdue
- **61-90 Days**: Significantly overdue
- **90+ Days**: Seriously overdue

### Summary Information
- **Total outstanding** amounts by age
- **Client breakdown** showing who owes what
- **Individual invoice details** with exact days overdue

## How to Use

### Generate Report
1. Go to **Reports** → **Invoice Aging**
2. **Select date** (defaults to today)
3. **View summary cards** for quick overview
4. **Expand client details** to see individual invoices

### Export Options
- **CSV export** for external analysis
- **Print-friendly** format for meetings
- **Custom date ranges** for historical analysis

## Business Value

- **Identify problem clients** with consistently late payments
- **Prioritize collection efforts** by age and amount
- **Track improvement** over time
- **Support cash flow planning**
      `,
      tags: ['aging', 'reports', 'cash-flow', 'collections'],
      lastUpdated: '2024-01-17',
      type: 'article'
    },
    {
      id: 'browser-requirements',
      title: 'Browser Requirements',
      category: 'Troubleshooting',
      description: 'Which browsers WorkOrder HQ supports, why Java is not needed, and how to run the live browser check.',
      content: `
# Browser Requirements

WorkOrder HQ is a modern website. It runs in the browser with JavaScript. **Java does not need to be installed** and will not fix search, uploads, or drag-and-drop.

## Recommended browsers

Use a current desktop version of:

- Google Chrome
- Microsoft Edge (the Chromium version that ships with Windows 10/11)
- Mozilla Firefox
- Safari 16 or newer on a Mac

Internet Explorer and the old (non-Chromium) Edge are not supported.

## Run the live check

On the Help Center home, open **Browser Check**. That page shows the detected browser, cookies, storage, file upload support, and a drag-and-drop test. Use **Copy report** if you need to send the results to support.

## File uploads

Drop files on the dashed upload box, or click **Choose Files**. If a dropped file opens in the browser instead of attaching, it was dropped outside that box — that is normal browser behavior, not a missing plugin.

## Search boxes

After typing in a list search box, wait a moment. Search runs after you pause, and it matches the fields shown in that list (for vendors: name, city, and state).
      `,
      tags: ['browser', 'chrome', 'edge', 'safari', 'java', 'javascript', 'upload', 'drag and drop', 'requirements'],
      lastUpdated: '2026-09-01',
      type: 'article'
    }
  ];

  const categories = ['all', ...Array.from(new Set(helpArticles.map(a => a.category)))];

  const filteredArticles = helpArticles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         article.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         article.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Getting Started': return <Compass className="h-4 w-4" />;
      case 'Invoices': return <BookOpen className="h-4 w-4" />;
      case 'Reports': return <BookOpen className="h-4 w-4" />;
      case 'Data & Security': return <Shield className="h-4 w-4" />;
      case 'Troubleshooting': return <Monitor className="h-4 w-4" />;
      default: return <HelpCircle className="h-4 w-4" />;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'video': return <Video className="h-4 w-4" />;
      case 'tutorial': return <BookOpen className="h-4 w-4" />;
      default: return <BookOpen className="h-4 w-4" />;
    }
  };

  if (selectedArticle) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="mb-6">
          <button
            onClick={() => setSelectedArticle(null)}
            className="text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 mb-4"
          >
            ← Back to Help
          </button>
          <div className="flex items-center gap-2 mb-2">
            {getTypeIcon(selectedArticle.type)}
            <span className="text-sm text-gray-500 dark:text-gray-400">{selectedArticle.type}</span>
            <span className="text-sm text-gray-500 dark:text-gray-400">•</span>
            <span className="text-sm text-gray-500 dark:text-gray-400">Updated {selectedArticle.lastUpdated}</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{selectedArticle.title}</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">{selectedArticle.description}</p>
        </div>
        
        <div className="prose prose-lg prose-gray dark:prose-invert max-w-none">
          <div className="help-content" dangerouslySetInnerHTML={{ 
            __html: formatHelpContent(selectedArticle.content) 
          }} />
        </div>
        
        <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Was this article helpful?</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <MessageCircle className="h-4 w-4 mr-2" />
                Give Feedback
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <style jsx>{`
        .help-content h1 {
          @apply text-3xl font-bold text-gray-900 dark:text-gray-100 mb-6 mt-8 first:mt-0 border-b border-gray-200 dark:border-gray-700 pb-3;
        }
        .help-content h2 {
          @apply text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4 mt-6 flex items-center;
        }
        .help-content h2:before {
          content: '';
          @apply w-1 h-6 bg-accent-color-500 mr-3 rounded;
        }
        .help-content h3 {
          @apply text-xl font-medium text-gray-900 dark:text-gray-100 mb-3 mt-5;
        }
        .help-content p {
          @apply mb-4 text-gray-700 dark:text-gray-300 leading-relaxed;
        }
        .help-content ul {
          @apply mb-4 space-y-2;
        }
        .help-content li {
          @apply text-gray-700 dark:text-gray-300 flex items-start;
        }
        .help-content li:before {
          content: '•';
          @apply text-accent-color-500 font-bold mr-3 mt-0.5;
        }
        .help-content ol {
          @apply mb-4 space-y-2;
        }
        .help-content ol li {
          @apply text-gray-700 dark:text-gray-300;
        }
        .help-content ol li:before {
          content: none;
        }
        .help-content strong {
          @apply font-semibold text-gray-900 dark:text-gray-100;
        }
        .help-content code {
          @apply bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 px-2 py-1 rounded text-sm font-mono;
        }
        .help-content blockquote {
          @apply border-l-4 border-accent-color-200 dark:border-accent-color-800 pl-4 py-2 bg-accent-color-50 dark:bg-accent-color-900/20 rounded-r;
        }
      `}</style>
      <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">Help Center</h1>
            <p className="text-gray-600 dark:text-gray-400">Find answers to common questions and learn how to use the system effectively.</p>
          </div>
          <Button
            onClick={startTour}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Compass className="h-4 w-4" />
            Take Tour
          </Button>
        </div>
        <button
          type="button"
          onClick={() => navigate('/help/browser-check')}
          className="w-full text-left bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow flex items-center justify-between gap-4"
        >
          <div className="flex items-start gap-3">
            <Monitor className="h-5 w-5 text-accent-color mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">Browser Check</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                See if this browser meets the requirements, and test file drag-and-drop. Java is not required.
              </p>
            </div>
          </div>
          <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
        </button>
      </div>

      {/* Search */}
      <div className="mb-8">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search help articles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Categories */}
      <div className="mb-8">
        <div className="flex flex-wrap gap-2">
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedCategory === category
                  ? 'bg-accent-color-100 text-accent-color-800 dark:bg-accent-color-800 dark:text-accent-color-200'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              {category === 'all' ? 'All Topics' : category}
            </button>
          ))}
        </div>
      </div>

      {/* Articles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredArticles.map(article => (
          <div
            key={article.id}
            onClick={() => setSelectedArticle(article)}
            className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow cursor-pointer"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                {getCategoryIcon(article.category)}
                <span className="text-sm text-gray-500 dark:text-gray-400">{article.category}</span>
              </div>
              {getTypeIcon(article.type)}
            </div>
            
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              {article.title}
            </h3>
            
            <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
              {article.description}
            </p>
            
            <div className="flex items-center justify-between">
              <div className="flex flex-wrap gap-1">
                {article.tags.slice(0, 3).map(tag => (
                  <span
                    key={tag}
                    className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <ChevronRight className="h-4 w-4 text-gray-400" />
            </div>
          </div>
        ))}
      </div>

      {/* No Results */}
      {filteredArticles.length === 0 && (
        <div className="text-center py-12">
          <HelpCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No articles found</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Try adjusting your search terms or browse different categories.
          </p>
          <Button variant="outline" onClick={() => { setSearchTerm(''); setSelectedCategory('all'); }}>
            Clear Filters
          </Button>
        </div>
      )}

      {/* Contact Support */}
      <div className="mt-12 bg-gray-50 dark:bg-gray-800 rounded-lg p-6">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Still need help?</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Can't find what you're looking for? Our support team is here to help.
          </p>
          <div className="flex justify-center gap-4">
            <Button variant="outline">
              <MessageCircle className="h-4 w-4 mr-2" />
              Contact Support
            </Button>
            <Button variant="outline">
              <ExternalLink className="h-4 w-4 mr-2" />
              Submit Feedback
            </Button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default HelpPage;
